import React, { useState } from 'react';
import { LuChevronDown, LuChevronRight, LuCircleCheckBig, LuTriangleAlert, LuUserRoundCog } from 'react-icons/lu';

import { RMDCalculationResult } from '@ndvr/portfolio-api';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatCurrency } from 'utils/number-formatter';
import { accountRoute } from 'utils/routes';

import { FulfillRmdModal } from './FulfillRmdModal';
import { getRmdStatus, RmdOwnerGroup } from './rmd-utils';
import { RmdBeneficiaryModal } from './RmdBeneficiaryModal';
import { RmdPlannedWithdrawals } from './RmdPlannedWithdrawals';
import { RmdTransactionHistory } from './RmdTransactionHistory';

interface RmdOwnerRowProps {
    owner: RmdOwnerGroup;
    taxYear: number;
}

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
    complete: { label: 'Complete', className: 'border-transparent bg-green-100 text-green-800' },
    'on-track': { label: 'On Track', className: 'border-transparent bg-blue-100 text-blue-800' },
    'at-risk': { label: 'At Risk', className: 'border-transparent bg-red-100 text-red-800' },
};

export const RmdOwnerRow: React.FC<RmdOwnerRowProps> = ({ owner, taxYear }) => {
    const [expanded, setExpanded] = useState(false);
    const [beneficiaryAccount, setBeneficiaryAccount] = useState<RMDCalculationResult | null>(null);
    const [fulfillModalOpen, setFulfillModalOpen] = useState(false);

    const accountGUIDs = owner.accounts.map((a) => a.accountGUID);
    const { totalRmdObligation, totalYtdSatisfied, totalPlannedWithdrawals } = owner;
    const status = getRmdStatus(totalRmdObligation, totalYtdSatisfied, totalPlannedWithdrawals);
    const badge = STATUS_BADGE[status];

    const hasYtd = totalYtdSatisfied !== undefined;
    const ytd = totalYtdSatisfied ?? 0;
    const actualPct = totalRmdObligation > 0 ? Math.min(100, (ytd / totalRmdObligation) * 100) : 0;
    const projectedPct =
        totalRmdObligation > 0 ? Math.min(100, ((ytd + totalPlannedWithdrawals) / totalRmdObligation) * 100) : 0;
    const remaining = Math.max(0, totalRmdObligation - ytd);
    const remainingProjected = hasYtd ? Math.max(0, totalRmdObligation - ytd - totalPlannedWithdrawals) : undefined;

    return (
        <>
            <TableRow className="cursor-pointer hover:bg-gray-50" onClick={() => setExpanded((v) => !v)}>
                <TableCell className="w-8 text-gray-400">
                    {expanded ? <LuChevronDown className="h-4 w-4" /> : <LuChevronRight className="h-4 w-4" />}
                </TableCell>
                <TableCell className="font-medium">
                    {owner.firstName} {owner.lastName}
                </TableCell>
                <TableCell className="text-right">{owner.age ?? '—'}</TableCell>
                <TableCell className="text-right">{owner.accounts.length}</TableCell>
                <TableCell className="text-right">
                    {owner.totalYearEndNav !== undefined ? formatCurrency(owner.totalYearEndNav) : '—'}
                </TableCell>
                <TableCell className="text-right">{formatCurrency(totalRmdObligation)}</TableCell>
                <TableCell className="text-right">{hasYtd ? formatCurrency(ytd) : '—'}</TableCell>
                <TableCell className="text-right">
                    {totalPlannedWithdrawals > 0 ? formatCurrency(totalPlannedWithdrawals) : '—'}
                </TableCell>
                <TableCell className="min-w-[200px]">
                    <div className="space-y-1.5">
                        <div className="space-y-0.5">
                            <div className="text-xs">
                                <span className="text-gray-500">Remaining (actual): </span>
                                <span className="font-medium text-gray-800">{formatCurrency(remaining)}</span>
                            </div>
                            <div className="text-xs">
                                <span className="text-gray-500">Remaining (projected w/ planned): </span>
                                <span className="font-medium text-gray-800">
                                    {remainingProjected !== undefined ? formatCurrency(remainingProjected) : '—'}
                                </span>
                            </div>
                        </div>
                        <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-gray-200">
                            <div
                                className="absolute inset-y-0 left-0 bg-green-500 transition-all"
                                style={{ width: `${actualPct}%` }}
                            />
                            {projectedPct - actualPct > 0 && (
                                <div
                                    className="absolute inset-y-0 bg-blue-200 transition-all"
                                    style={{ left: `${actualPct}%`, width: `${projectedPct - actualPct}%` }}
                                />
                            )}
                        </div>
                    </div>
                </TableCell>
                <TableCell>
                    <Badge className={badge.className}>{badge.label}</Badge>
                </TableCell>
                <TableCell className="w-10">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setFulfillModalOpen(true);
                                }}
                            >
                                <LuCircleCheckBig className="h-4 w-4" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>Fulfill remaining RMD</TooltipContent>
                    </Tooltip>
                </TableCell>
            </TableRow>

            {expanded && (
                <TableRow className="bg-gray-50 hover:bg-gray-50">
                    <TableCell colSpan={11} className="p-0">
                        <div className="px-6 py-4">
                            <h4 className="mb-3 text-sm font-semibold text-gray-700">Underlying Accounts</h4>
                            <div className="rounded-md border bg-white">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-gray-50">
                                            <TableHead className="text-xs font-medium text-gray-500">Account</TableHead>
                                            <TableHead className="text-xs font-medium text-gray-500">
                                                Custodian
                                            </TableHead>
                                            <TableHead className="text-right text-xs font-medium text-gray-500">
                                                Current Balance
                                            </TableHead>
                                            <TableHead className="text-right text-xs font-medium text-gray-500">
                                                Prior Yr Balance
                                            </TableHead>
                                            <TableHead className="text-right text-xs font-medium text-gray-500">
                                                RMD Amount
                                            </TableHead>
                                            <TableHead className="text-right text-xs font-medium text-gray-500">
                                                Gross Dist. YTD
                                            </TableHead>
                                            <TableHead className="text-right text-xs font-medium text-gray-500">
                                                Planned Gross Dist.
                                            </TableHead>
                                            <TableHead className="w-10" />
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {owner.accounts.map((acct: RMDCalculationResult) => {
                                            const acctPlanned = (acct.withdrawals ?? []).reduce(
                                                (s, w) => s + parseFloat(w.finalAmount),
                                                0,
                                            );
                                            return (
                                                <TableRow key={acct.accountGUID}>
                                                    <TableCell className="text-sm">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <div>
                                                                <a
                                                                    href={accountRoute(acct.accountGUID)}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className="font-medium hover:underline"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    {acct.accountName ?? acct.pId}
                                                                </a>
                                                                {acct.custodyAccountId && (
                                                                    <span className="ml-2 text-xs text-gray-400">
                                                                        ****{acct.custodyAccountId.slice(-4)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {acct.inherited && (
                                                                <Badge className="border-purple-500/50 bg-purple-50 px-1.5 py-0 text-[10px] text-purple-600">
                                                                    Inherited IRA
                                                                </Badge>
                                                            )}
                                                            {acct.inFirstRmdYear && (
                                                                <Badge className="border-amber-500/50 bg-amber-50 px-1.5 py-0 text-[10px] text-amber-600">
                                                                    First RMD Year (deferral available)
                                                                </Badge>
                                                            )}
                                                            {acct.inDeferredYear && (
                                                                <Badge className="border-orange-500/50 bg-orange-50 px-1.5 py-0 text-[10px] text-orange-600">
                                                                    In Deferral Year
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="text-sm text-gray-600">
                                                        {acct.custodyType ?? '—'}
                                                    </TableCell>
                                                    <TableCell className="text-right text-sm">
                                                        {acct.currentNav
                                                            ? formatCurrency(parseFloat(acct.currentNav))
                                                            : '—'}
                                                    </TableCell>
                                                    <TableCell className="text-right text-sm">
                                                        {acct.yearEndNav
                                                            ? formatCurrency(parseFloat(acct.yearEndNav))
                                                            : '—'}
                                                    </TableCell>
                                                    <TableCell className="text-right text-sm">
                                                        {formatCurrency(parseFloat(acct.rmdObligation))}
                                                    </TableCell>
                                                    <TableCell className="text-right text-sm">
                                                        {formatCurrency(parseFloat(acct.ytdRmdSatisfied ?? '0'))}
                                                    </TableCell>
                                                    <TableCell className="text-right text-sm">
                                                        {acctPlanned > 0 ? formatCurrency(acctPlanned) : '—'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {acct.inherited && (
                                                            <div className="flex items-center gap-1">
                                                                {(!acct.decedentDateOfDeath ||
                                                                    !acct.beneficiaryType) && (
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <span>
                                                                                <LuTriangleAlert className="h-3.5 w-3.5 text-amber-500" />
                                                                            </span>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent>
                                                                            Beneficiary & decedent info incomplete
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                )}
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            className="h-7 w-7"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setBeneficiaryAccount(acct);
                                                                            }}
                                                                        >
                                                                            <LuUserRoundCog className="h-3.5 w-3.5 text-gray-400" />
                                                                        </Button>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        Beneficiary & Decedent Info
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                        <TableRow className="border-t-2 bg-gray-50 font-semibold">
                                            <TableCell className="text-sm" colSpan={4}>
                                                Total
                                            </TableCell>
                                            <TableCell className="text-right text-sm">
                                                {formatCurrency(totalRmdObligation)}
                                            </TableCell>
                                            <TableCell className="text-right text-sm">
                                                {hasYtd ? formatCurrency(ytd) : '—'}
                                            </TableCell>
                                            <TableCell className="text-right text-sm">
                                                {totalPlannedWithdrawals > 0
                                                    ? formatCurrency(totalPlannedWithdrawals)
                                                    : '—'}
                                            </TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </div>

                            {/* Transaction History + Planned Withdrawals side by side */}
                            <div className="mt-4 grid grid-cols-2 items-start gap-4">
                                <RmdTransactionHistory
                                    accountGUIDs={accountGUIDs}
                                    accounts={owner.accounts}
                                    taxYear={taxYear}
                                    defaultOpen={false}
                                />
                                <RmdPlannedWithdrawals
                                    withdrawals={owner.accounts.flatMap((a) => a.withdrawals ?? [])}
                                    defaultOpen={false}
                                />
                            </div>
                        </div>
                    </TableCell>
                </TableRow>
            )}
            {beneficiaryAccount && (
                <RmdBeneficiaryModal
                    open={!!beneficiaryAccount}
                    onOpenChange={(open) => {
                        if (!open) setBeneficiaryAccount(null);
                    }}
                    account={beneficiaryAccount}
                />
            )}
            <FulfillRmdModal open={fulfillModalOpen} onOpenChange={setFulfillModalOpen} owner={owner} />
        </>
    );
};
